/**
 * sourceAttribution — canonical acquisition-source normalization.
 *
 * Shared by the client-side tracker (write path) and the admin Metrics
 * dashboard (read path) so historical rows written with raw labels such as
 * "fb" / "Facebook" / "l.facebook.com" report under one canonical source.
 *
 * Analytics-only module: it never influences auth, billing or app behavior.
 */

export const UNKNOWN_SOURCE = "Unknown / Historical Attribution Unavailable";
export const INTERNAL_SOURCE = "Internal/Preview";
export const DIRECT_SOURCE = "Direct";

export type SourceKind =
  /** A real external acquisition source (Facebook, TikTok, a blog, …). */
  | "external"
  /** No identifiable external source — typed the URL, bookmark, etc. */
  | "direct"
  /** Lovable preview / dev / localhost traffic. Excluded from marketing math. */
  | "internal"
  /** A LupusLogix page referring to another LupusLogix page. Never a source. */
  | "self"
  /**
   * A service the visitor was sent to and bounced back from (Stripe Checkout,
   * Google/YouTube account screens). Never an acquisition source — it must not
   * overwrite an already-established first touch.
   */
  | "service"
  /** Historical row with no reconstructable first touch. */
  | "unknown";

export type CanonicalSource = { source: string; kind: SourceKind };

/** Hosts (and host suffixes) that are our own property — self-referrals. */
const SELF_HOSTS = ["lupuslogix.com", "lupuslogix.app"];

/**
 * Service-return hosts. A referral from these means the visitor came BACK from
 * a service we sent them to (checkout, OAuth consent), not that the service
 * acquired them.
 */
const SERVICE_RETURN_HOSTS = [
  "checkout.stripe.com",
  "billing.stripe.com",
  "pay.stripe.com",
  "stripe.com",
  "accounts.google.com",
  "accounts.youtube.com",
  "myaccount.google.com",
  "consent.google.com",
  "appleid.apple.com",
];

/** Hosts that are development / preview surfaces, never public marketing. */
const INTERNAL_HOST_PATTERNS = [
  "lovable.dev",
  "lovableproject.com",
  "lovable.app",
  "localhost",
  "127.0.0.1",
  "id-preview--",
];

const stripHost = (value: string) =>
  value.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];

const isSelfHost = (host: string) =>
  SELF_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));

const isInternalHost = (host: string) =>
  INTERNAL_HOST_PATTERNS.some((p) => host.includes(p));

const isServiceReturnHost = (host: string) =>
  SERVICE_RETURN_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));

/**
 * Case-insensitive alias table. Keys are lowercased raw labels (utm_source
 * values or referrer hostnames); values are the canonical display name.
 */
const ALIASES: Record<string, string> = {
  // Facebook / Meta
  fb: "Facebook",
  facebook: "Facebook",
  meta: "Facebook",
  // Meta Audience Network (utm_source=an) — Meta traffic shown separately.
  an: "Meta Audience Network",
  audience_network: "Meta Audience Network",
  "facebook.com": "Facebook",
  "m.facebook.com": "Facebook",
  "l.facebook.com": "Facebook",
  "lm.facebook.com": "Facebook",
  "web.facebook.com": "Facebook",
  "business.facebook.com": "Facebook",
  fbclid: "Facebook",
  // Instagram
  ig: "Instagram",
  instagram: "Instagram",
  "instagram.com": "Instagram",
  "l.instagram.com": "Instagram",
  // TikTok
  tiktok: "TikTok",
  "tiktok.com": "TikTok",
  "vt.tiktok.com": "TikTok",
  "www.tiktok.com": "TikTok",
  // Google
  google: "Google",
  "google.com": "Google",
  "news.google.com": "Google",
  // Other common networks
  youtube: "YouTube",
  "youtube.com": "YouTube",
  "youtu.be": "YouTube",
  yt: "YouTube",
  "m.youtube.com": "YouTube",
  "www.youtube.com": "YouTube",
  "music.youtube.com": "YouTube",
  "youtube-shorts": "YouTube",
  shorts: "YouTube",
  x: "X/Twitter",
  twitter: "X/Twitter",
  "twitter.com": "X/Twitter",
  "t.co": "X/Twitter",
  "x.com": "X/Twitter",
  linkedin: "LinkedIn",
  "linkedin.com": "LinkedIn",
  "lnkd.in": "LinkedIn",
  reddit: "Reddit",
  "reddit.com": "Reddit",
  pinterest: "Pinterest",
  "pinterest.com": "Pinterest",
  bing: "Bing",
  "bing.com": "Bing",
  "www.bing.com": "Bing",
  "cn.bing.com": "Bing",
  msn: "Bing",
  "msn.com": "Bing",
  yahoo: "Yahoo",
  "yahoo.com": "Yahoo",
  "search.yahoo.com": "Yahoo",
  "r.search.yahoo.com": "Yahoo",
  "us.search.yahoo.com": "Yahoo",
  "duckduckgo.com": "DuckDuckGo",
  duckduckgo: "DuckDuckGo",
  ddg: "DuckDuckGo",
  "duck.com": "DuckDuckGo",
  email: "Email",
  newsletter: "Email",
  "e-mail": "Email",
  mail: "Email",
  gmail: "Email",
  "mail.google.com": "Email",
  outlook: "Email",
  "outlook.live.com": "Email",
  "outlook.office.com": "Email",
  resend: "Email",
  flyer_email: "Email",
  brochure_email: "Email",
  setup_reminder: "Email (Setup Reminder)",
  // Offline / printed materials scanned via QR code
  qr: "Print / QR",
  qrcode: "Print / QR",
  "qr-code": "Print / QR",
  qr_code: "Print / QR",
  print: "Print / QR",
  flyer: "Print / QR",
  brochure: "Print / QR",
  trifold: "Print / QR",
  "tri-fold": "Print / QR",
  poster: "Print / QR",
  card: "Print / QR",
  businesscard: "Print / QR",
  business_card: "Print / QR",
  direct: DIRECT_SOURCE,
};

const titleCase = (value: string) =>
  value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

/**
 * Normalize any raw source label (utm_source value or referrer host) into a
 * canonical source + kind.
 */
export const canonicalizeSource = (raw: string | null | undefined): CanonicalSource => {
  if (!raw || !raw.trim()) return { source: UNKNOWN_SOURCE, kind: "unknown" };
  const value = raw.trim().toLowerCase();

  const mapped = ALIASES[value];
  if (mapped) {
    return { source: mapped, kind: mapped === DIRECT_SOURCE ? "direct" : "external" };
  }

  const host = stripHost(value);
  if (isInternalHost(host)) return { source: INTERNAL_SOURCE, kind: "internal" };
  // Checked BEFORE the alias table so e.g. accounts.youtube.com is never
  // credited to YouTube as an acquisition source.
  if (isServiceReturnHost(host)) return { source: DIRECT_SOURCE, kind: "service" };
  if (isSelfHost(host)) return { source: DIRECT_SOURCE, kind: "self" };

  const hostAlias = ALIASES[host];
  if (hostAlias) {
    return { source: hostAlias, kind: hostAlias === DIRECT_SOURCE ? "external" : "external" };
  }

  // Unrecognized but genuinely external (a real referring domain or a custom
  // utm_source we haven't aliased). Keep it — just tidy the label.
  return { source: host.includes(".") ? host : titleCase(value), kind: "external" };
};

/** True when the label represents preview/dev traffic. */
export const isInternalSourceLabel = (raw: string | null | undefined): boolean =>
  canonicalizeSource(raw).kind === "internal";

/** True for a hostname that is one of our own public domains. */
export const isSelfReferralHost = (host: string): boolean => isSelfHost(stripHost(host));

/** True for preview/dev hostnames. */
export const isInternalHostname = (host: string): boolean => isInternalHost(stripHost(host));

/** True for hosts we return FROM (Stripe/Google) — never an acquisition source. */
export const isServiceReturnHostname = (host: string): boolean =>
  isServiceReturnHost(stripHost(host));